import { useContext, useEffect, useRef } from 'react';
import { TrackingContext } from './Tracking.context';
import { AppContext } from '../App.context';
import { TrackerLogger } from './Tracking.events';
import { MetaPixelTracker } from './trackers/MetaPixelTracker';
import MatomoTracker from '@canvasflow/analytics/src/tracker/tracker';
import { useRouterState } from '@tanstack/react-router';

const IS_INSTALLED_KEY = 'pwa-installed';

declare global {
	interface Window {
		dataLayer: any;
		gtag: any;
	}
}

export function useTrackingPageView({
	enabled,
	data,
	delay = 500,
}: TrackingPageViewProps) {
	const { gtag, gtm, locationChannel, matomo, plausible, metaPixel } =
		useContext(TrackingContext);
	// 🔥 Get real router location (source of truth)
	const routerState = useRouterState();
	const pathname = routerState.location.pathname;

	// allow override if passed
	const location = pathname;

	// ✅ Track last successful event
	const lastTrackedRef = useRef<string | null>(null);
	const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	useEffect(() => {
		if (!enabled || !location) return;

		// 🚫 prevent duplicate scheduling
		if (lastTrackedRef.current === location) return;

		// 🧹 clear previous timeout (important for fast navigation)
		if (timeoutRef.current) {
			clearTimeout(timeoutRef.current);
		}

		timeoutRef.current = setTimeout(() => {
			const title = document.title;

			// 🚫 double-check before firing (in case navigation changed again)
			if (lastTrackedRef.current === location) return;

			const previousPath = lastTrackedRef.current;

			lastTrackedRef.current = location;

			TrackerLogger.debug(`Page view "${location}"`, {
				title,
				location,
				previousPath,
				data,
			});

			locationChannel.postMessage(location);

			if (gtag && window.gtag) {
				// ga.pageview(location, undefined, title);
				window.gtag('event', 'page_view', {
					page_title: title,
					page_location: location,
				});
			}

			if (gtm && window.dataLayer) {
				const gtmEvent = {
					event: 'pageview',
					page: {
						url: location,
						title: title,
					},
				};

				window.dataLayer.push(gtmEvent);
			}

			if (matomo) {
				TrackPageView.sendToMatomo(title, location, matomo, data);
			}
			if (plausible) {
				const url = window.location.href;
				plausible.trackPageview(
					{
						url,
					},
					{
						callback: () =>
							console.log(
								`Plausible done sending page view: ${url}`,
							),
					},
				);
			}

			if (metaPixel) {
				console.log(`I sent the MetaPixel page view event`);
				MetaPixelTracker.pageView();
			}
		}, delay);
		// 🧹 cleanup on unmount or route change
		return () => {
			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current);
			}
		};
	}, [location, enabled, delay]);
}

class TrackPageView {
	static sendToMatomo(
		title: string,
		location: string,
		matomo: MatomoTracker,
		data?: any,
	) {
		const customDimensions: Array<{ id: number; value: string }> = [];
		const pageView: {
			title: string;
			location: string;
			customDimensions?: Array<{ id: number; value: string }>;
		} = {
			title,
			location,
		};

		if (data) {
			if (data?.language) {
				customDimensions.push({
					id: 9,
					value: `${data?.language}`,
				});
			}
			if (data?.issue?.id) {
				customDimensions.push({
					id: 6,
					value: `${data?.issue?.id}`,
				});
			}
			if (data?.article?.id) {
				customDimensions.push({
					id: 7,
					value: `${data.article.id}`,
				});
			}
		}

		if (customDimensions.length) {
			pageView.customDimensions = customDimensions;
		}
		console.debug(`Matomo page view: `, pageView);
		matomo.trackPageView(pageView);
	}
}

export function useTrackingInstallion() {
	const { sendEvent } = useContext(TrackingContext);
	const { isInstalled } = useContext(AppContext);
	useEffect(() => {
		if (localStorage.getItem(IS_INSTALLED_KEY)) {
			return;
		}

		if (!isInstalled) {
			return;
		}

		localStorage.setItem(IS_INSTALLED_KEY, 'true');
		sendEvent({
			type: 'INSTALLED',
		});
	}, [isInstalled]);
}

interface TrackingPageViewProps {
	enabled: boolean;
	data?: {
		language?: string;
		issue?: any;
		article?: any;
	};
	delay?: number;
}
